import AddIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Button, Container, IconButton, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../api/adminApi";
import type {
  EntityFieldType,
  ObjectChecklist,
  ObjectClassDefinition,
  ObjectFieldValues,
} from "../../../../api/types/objectTypes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import NewObjectDialog from "../../../entityFields/NewObjectDialog";
import ObjectChecklistConditionEditor from "../../../object-filters/ObjectChecklistConditionEditor";
import { getChecklistConditionFieldGroups } from "../../../object-filters/objectFilterHelpers";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";
import type { ChecklistConfigurationState, ChecklistEditFormValidation } from "./checklistConfigurationState";
import {
  addConditionFilterAction,
  addTaskAction,
  changeChecklistNameAction,
  goBackToListAction,
  initialChecklistEditFormValidation,
  isChecklistFormDirty,
  removeConditionFilterAction,
  resetChecklistFormAction,
  setConditionAction,
  startAddingChecklistAction,
  startEditingChecklistAction,
  validateChecklistEditForm,
} from "./checklistConfigurationState";
import ChecklistTasksTable from "./ChecklistTasksTable";

const supportedFieldTypes: EntityFieldType[] = [
  "Text",
  "Email",
  "Phone",
  "Url",
  "Number",
  "Money",
  "Percent",
  "Date",
  "DateTime",
  "Checkbox",
  "UserDefinedOptionsSelect",
];

interface Props {
  state: ChecklistConfigurationState;
  setState: Dispatch<SetStateAction<ChecklistConfigurationState>>;
  taskTemplates: ObjectFieldValues[];
  relatedObjectDefinitions: Record<string, ObjectClassDefinition>;
  onTaskTemplateCreated: (newTaskTemplate: ObjectFieldValues) => void;
}

const addObjectChecklist = withErrorHandling(adminApi.addObjectChecklist);
const updateObjectChecklist = withErrorHandling(adminApi.updateObjectChecklist);

const ChecklistEditView = ({
  state,
  setState,
  taskTemplates,
  relatedObjectDefinitions,
  onTaskTemplateCreated,
}: Props) => {
  const { objectDefinition, onUpdateObjectDefinition } = useObjectDefinitionContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState<ChecklistEditFormValidation>(initialChecklistEditFormValidation);
  const [showTaskSearch, setShowTaskSearch] = useState(false);
  const [newTaskTemplateDialogOpen, setNewTaskTemplateDialogOpen] = useState(false);

  const availableFields = useMemo(
    () => getChecklistConditionFieldGroups(objectDefinition, relatedObjectDefinitions, supportedFieldTypes),
    [objectDefinition, relatedObjectDefinitions]
  );

  const taskTemplateOptions = useMemo(
    () =>
      taskTemplates
        .filter((t) => !state.form.taskTemplateIds.includes(t.id))
        .sort(stringComparerBy((t) => t.displayValues.primaryValue)),
    [state.form.taskTemplateIds, taskTemplates]
  );

  const isAddMode = state.view === "add";

  const storedChecklist = objectDefinition.checklists.find((c) => c.id === state.editedChecklistId);

  const isFormDirty = isAddMode || (storedChecklist !== undefined && isChecklistFormDirty(state.form, storedChecklist));

  const handleBack = () => setState(goBackToListAction());

  const handleCancel = () => {
    if (isAddMode) {
      setState(startAddingChecklistAction());
    } else if (storedChecklist) {
      setState(resetChecklistFormAction(storedChecklist));
    }
    setValidation(initialChecklistEditFormValidation);
  };

  const handleAdd = async () => {
    const [updatedChecklists, error] = await addObjectChecklist(objectDefinition.objectType, {
      name: state.form.name.trim(),
      conditions: state.form.conditions,
      taskTemplateIds: state.form.taskTemplateIds,
    });

    if (error) {
      logError(error, "[ChecklistEditView] addObjectChecklist");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Checklist created");
    onUpdateObjectDefinition({ ...objectDefinition, checklists: updatedChecklists });
    const existingIds = new Set(objectDefinition.checklists.map((c) => c.id));
    const newChecklist = updatedChecklists.find((c: ObjectChecklist) => !existingIds.has(c.id));
    if (newChecklist) {
      setState(startEditingChecklistAction(newChecklist));
    }
  };

  const handleEdit = async () => {
    if (!storedChecklist) {
      return;
    }

    const [updatedChecklists, error] = await updateObjectChecklist(objectDefinition.objectType, storedChecklist.id, {
      name: state.form.name.trim(),
      conditions: state.form.conditions,
      taskTemplateIds: state.form.taskTemplateIds,
    });

    if (error) {
      logError(error, "[ChecklistEditView] updateObjectChecklist");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Checklist updated");
    onUpdateObjectDefinition({ ...objectDefinition, checklists: updatedChecklists });
  };

  const handleSave = async () => {
    const result = validateChecklistEditForm(state.form);
    setValidation(result);
    if (!result.isValid) {
      return;
    }

    setSaving(true);
    try {
      await (isAddMode ? handleAdd() : handleEdit());
    } finally {
      setSaving(false);
    }
  };

  const handleTaskTemplateSelected = (taskTemplateId: string) => {
    setState(addTaskAction(taskTemplateId));
    setShowTaskSearch(false);
  };

  const handleNewTaskTemplateCreated = (newTaskTemplate: ObjectFieldValues) => {
    setNewTaskTemplateDialogOpen(false);
    handleTaskTemplateSelected(newTaskTemplate.id);
    onTaskTemplateCreated(newTaskTemplate);
  };

  return (
    <>
      <GeneralPageHeader
        BackButton={
          <IconButton onClick={handleBack}>
            <ArrowBackRoundedIcon />
          </IconButton>
        }
        TitleComponent={
          <Stack spacing={0.5} sx={{ py: 1.5 }}>
            <Typography variant="h6">{isAddMode && !state.form.name ? "New Checklist" : state.form.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              Checklist
            </Typography>
          </Stack>
        }
      >
        <Button variant="text" color="secondary" onClick={handleCancel} disabled={saving || !isFormDirty}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" loading={saving} disabled={!isFormDirty} onClick={handleSave}>
          Save
        </Button>
      </GeneralPageHeader>
      <Container maxWidth="md" disableGutters sx={{ height: "100%", py: 2.5 }}>
        <ScrollableFlexContainer sx={{ height: "100%" }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ pt: 2 }}>
              General
            </Typography>
            <TextField
              label="Name"
              fullWidth
              value={state.form.name}
              onChange={(e) => setState(changeChecklistNameAction(e.target.value))}
              error={!validation.name.isValid}
              helperText={!validation.name.isValid ? validation.name.error : undefined}
            />
            <Typography variant="subtitle1">Conditions</Typography>
            {!validation.condition.overall.isValid && (
              <Typography variant="caption" color="error">
                {validation.condition.overall.error}
              </Typography>
            )}
            {state.form.conditions.map((c, i) => (
              <Stack key={state.conditionKeys[i]} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <ObjectChecklistConditionEditor
                  condition={c}
                  availableFields={availableFields}
                  onConditionChange={(condition) => setState(setConditionAction(i, condition))}
                  startLabel={i === 0 ? "Where" : "And"}
                  error={
                    validation.condition.filters[i]?.isValid === false
                      ? validation.condition.filters[i]?.error
                      : undefined
                  }
                />
                <IconButton size="small" onClick={() => setState(removeConditionFilterAction(i))}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setState(addConditionFilterAction())}
              sx={{ alignSelf: "flex-start" }}
            >
              Add Condition
            </Button>
            <Typography variant="subtitle1">Tasks</Typography>
            {!validation.taskTemplateIds.isValid && (
              <Typography variant="caption" color="error">
                {validation.taskTemplateIds.error}
              </Typography>
            )}
            <ChecklistTasksTable state={state} setState={setState} taskTemplates={taskTemplates} />
            {showTaskSearch ? (
              <AutocompleteCreatable
                options={taskTemplateOptions}
                getOptionLabel={(t) => t.displayValues.primaryValue}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={null}
                onChange={(_, val) => {
                  if (val) {
                    handleTaskTemplateSelected(val.id);
                  }
                }}
                renderInput={(params) => <TextField {...params} placeholder="Type task name..." autoFocus />}
                renderOption={({ key: _key, ...props }, option) => (
                  <ListItem key={option.id} {...props} dense>
                    <ListItemText
                      primary={option.displayValues.primaryValue}
                      secondary={option.displayValues.secondaryValue || "No category"}
                    />
                  </ListItem>
                )}
                noItemsText="No tasks found"
                openOnFocus
                onClose={() => setShowTaskSearch(false)}
                onCreateOption={() => setNewTaskTemplateDialogOpen(true)}
              />
            ) : (
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setShowTaskSearch(true)}
                sx={{ alignSelf: "flex-start" }}
              >
                Add Task
              </Button>
            )}
          </Stack>
        </ScrollableFlexContainer>
      </Container>
      {newTaskTemplateDialogOpen && (
        <NewObjectDialog
          objectType="ChecklistTaskTemplate"
          onClose={() => setNewTaskTemplateDialogOpen(false)}
          onCreated={handleNewTaskTemplateCreated}
        />
      )}
    </>
  );
};

export default ChecklistEditView;
